'use client'

import { useState } from 'react'
import { Check, Copy, Link2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { Input } from '@/components/ui/input'
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemTitle,
} from '@/components/ui/item'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { formatDateTime } from '@/lib/format/date'
import { useCaseUploadLinks } from '@/features/cases/hooks/use-case-detail'
import {
  useCreateCaseUploadLink,
  useRevokeCaseUploadLink,
} from '@/features/cases/hooks/use-case-document-writes'
import type { CaseStatus, CaseUploadLinkCreated } from '@/features/cases/types'

function isExpired(expiresAt: string) {
  return new Date(expiresAt).getTime() < Date.now()
}

/** The reveal-once dialog for a freshly generated link — the raw token is only ever returned
 * here; a lost link can only be revoked-and-recreated, never recovered. */
function LinkRevealDialog({
  created,
  onOpenChange,
}: {
  created: CaseUploadLinkCreated | null
  onOpenChange: (open: boolean) => void
}) {
  const [copied, setCopied] = useState(false)
  const url = created ? `${window.location.origin}/upload/${created.token}` : ''

  const copyUrl = async () => {
    if (!url) return
    await navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Dialog
      open={!!created}
      onOpenChange={(open) => {
        if (!open) setCopied(false)
        onOpenChange(open)
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload link created</DialogTitle>
          <DialogDescription>
            Share this with the investor — it&apos;s shown once and isn&apos;t stored anywhere.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 py-2">
          <Input readOnly value={url} className="font-mono text-xs" />
          <Button type="button" variant="outline" size="icon" onClick={copyUrl}>
            {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
          </Button>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button">Done</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Staff-only upload-link generate/list/revoke — no mock counterpart exists at all (the investor
 * dashboard has no concept of "links I generated for myself"), so this calls the real hooks
 * directly rather than the injected-callback pattern the rest of this feature uses.
 */
export function CaseUploadLinks({
  caseId,
  caseStatus,
}: {
  caseId: string
  caseStatus: CaseStatus
}) {
  const { data: links = [] } = useCaseUploadLinks(caseId)
  const createLink = useCreateCaseUploadLink(caseId)
  const revokeLink = useRevokeCaseUploadLink(caseId)
  const [reveal, setReveal] = useState<CaseUploadLinkCreated | null>(null)
  const isPublished = caseStatus === 'published'

  const handleGenerate = async () => {
    const created = await createLink.mutateAsync()
    setReveal(created)
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium">Upload links</p>
        <Tooltip>
          {/* Wrapped in a span, not applied to the button directly — a disabled button doesn't
              reliably fire hover events in every browser, so a plain `title` on it never shows
              (same reasoning as the Publish-step tooltip a few hundred lines up in
              `client-detail.tsx`). The trigger needs a non-disabled element around it. */}
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={createLink.isPending || isPublished}
                onClick={() => void handleGenerate()}
              >
                <Link2 className="size-3.5" /> Generate upload link
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {isPublished
              ? 'This case is already published — the investor already has full dashboard access.'
              : 'Generate a link the investor can use to upload without signing in'}
          </TooltipContent>
        </Tooltip>
      </div>

      {links.length === 0 ? (
        <Empty className="border border-dashed py-8">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Link2 />
            </EmptyMedia>
            <EmptyTitle>No upload links yet</EmptyTitle>
            <EmptyDescription>
              Generate one to let the investor send documents without signing in.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <ItemGroup className="gap-2">
          {links.map((link) => {
            const revoked = !!link.revokedAt
            const expired = !revoked && isExpired(link.expiresAt)
            return (
              <Item key={link.id} variant="outline" size="sm">
                <ItemContent>
                  <ItemTitle>Created {formatDateTime(link.createdAt)}</ItemTitle>
                  <ItemDescription>
                    {revoked
                      ? 'Revoked'
                      : expired
                        ? 'Expired'
                        : `Expires ${formatDateTime(link.expiresAt)}`}
                    {link.firstUsedAt ? ` · first used ${formatDateTime(link.firstUsedAt)}` : ''}
                  </ItemDescription>
                </ItemContent>
                <ItemActions className="gap-1.5">
                  {(revoked || expired) && (
                    <Badge variant={revoked ? 'destructive' : 'outline'}>
                      {revoked ? 'Revoked' : 'Expired'}
                    </Badge>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={revoked || expired || revokeLink.isPending}
                    onClick={() => revokeLink.mutate(link.id)}
                  >
                    Revoke
                  </Button>
                </ItemActions>
              </Item>
            )
          })}
        </ItemGroup>
      )}

      <LinkRevealDialog created={reveal} onOpenChange={(open) => !open && setReveal(null)} />
    </div>
  )
}
